/**
 * InsurancePage — feature-parity mirror of the Assets/LoansPage:
 *   • hamburger toolbar with Custom KPI, Bulk upload, Add/Edit mode, Export Excel/PDF, Reminder flags, Email reminders
 *   • Add column + Restore custom columns
 *   • active / archived / deleted tables (soft delete flow)
 *   • per-row Archive button; archived rows can be Restored or Deleted
 *   • Deleted table supports Restore + Purge
 *   • Expiration Legend KPI + coloured rows by days-to-expiry
 *   • MM/DD/YYYY dates for expiry/start/end
 *   • datalist autocomplete + fuzzy typo-warning modal on 9 text columns
 *
 * All backend endpoints live in ``/api/insurance*``; when running without
 * the ``insurance_access`` permission the page renders a locked-out screen.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import * as XLSX from "xlsx";
import html2pdf from "html2pdf.js";
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter } from "@dnd-kit/core";
import {
  SortableContext, useSortable, horizontalListSortingStrategy, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import api, { apiErrorMessage } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import AppHeader from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  MagnifyingGlass, Plus, Archive, FileXls, FilePdf, Bell, PencilSimple, DotsSixVertical, X, ArrowClockwise,
  List as ListIcon, CaretDown, CaretUp, ArrowsDownUp, Paperclip, WarningCircle,
} from "@phosphor-icons/react";
import InsuranceModal from "@/components/insurance/InsuranceModal";
import { TYPO_COLS, findTypoMatch, TypoWarningDialog } from "@/components/insurance/TypoWarning";
import AddColumnModal from "@/components/insurance/AddColumnModal";
import RestoreColumnsModal from "@/components/insurance/RestoreColumnsModal";
import BulkUploadModal from "@/components/insurance/BulkUploadModal";
import ReminderConfigModal from "@/components/insurance/ReminderConfigModal";

const COL_ORDER_LS_KEY = "strata.insurance.columnOrder.v1";


// Column definitions (id, label, type). Ordering here = default table order.
// Insurance ID is pinned first (rendered outside the draggable set).
const DEFAULT_COLS = [
  { id: "insured_asset",   label: "Insured Asset",  type: "text", autocomplete: true },
  { id: "insured_entity",  label: "Insured Entity", type: "text", autocomplete: true },
  { id: "type",            label: "Type",           type: "text", autocomplete: true },
  { id: "category",        label: "Category",       type: "text", autocomplete: true },
  { id: "type_code",       label: "Type Code",      type: "text", autocomplete: true },
  { id: "expiry_date",     label: "Expiry Date",    type: "date" },
  { id: "broker",          label: "Broker",         type: "text", autocomplete: true },
  { id: "broker_phone",    label: "Broker Phone",   type: "text", autocomplete: true },
  { id: "broker_email",    label: "Broker Email",   type: "text", autocomplete: true },
  { id: "carrier",         label: "Carrier",        type: "text", autocomplete: true },
  { id: "financing",       label: "Financing",      type: "text" },
  { id: "start_date",      label: "Start Date",     type: "date" },
  { id: "end_date",        label: "End Date",       type: "date" },
  { id: "coverage_time",   label: "Coverage Time",  type: "months" },
  { id: "premium_price",   label: "Premium Price",  type: "money" },
  { id: "policy_number",   label: "Policy Number",  type: "text" },
  { id: "full_address",    label: "Full Address",   type: "text" },
];

// ---- formatting ----
const fmtMoney = (n) => {
  if (n == null || n === "" || Number.isNaN(Number(n))) return "";
  return `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};
// MM/DD/YYYY as requested by product.
const fmtDate = (s) => {
  if (!s) return "";
  const d = new Date(String(s).length <= 10 ? String(s) + "T00:00:00" : String(s));
  if (Number.isNaN(d.getTime())) return "";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};
const fmtLU = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d)) return "";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);
  return `${mm}.${dd}.${yy}`;
};

function coverageMonths(p) {
  const explicit = Number(p.coverage_time);
  if (Number.isFinite(explicit) && explicit > 0) return explicit;
  if (p.start_date && p.end_date) {
    const s = new Date(p.start_date + "T00:00:00");
    const e = new Date(p.end_date + "T00:00:00");
    if (!Number.isNaN(s) && !Number.isNaN(e) && e > s) {
      const ms = e - s;
      return Math.max(0.1, ms / (1000 * 60 * 60 * 24 * 30.4375));
    }
  }
  return null;
}

// Returns { key, cls, rowCls, label } for expiry pill + row highlight.
function expiryTone(iso) {
  if (!iso) return null;
  const d = new Date(String(iso).length <= 10 ? String(iso) + "T00:00:00" : String(iso));
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diffDays = Math.round((d - today) / (1000 * 60 * 60 * 24));
  if (diffDays < 0)  return { key: "expired", cls: "bg-rose-600 text-white",                                   rowCls: "bg-rose-100/70",   label: `${Math.abs(diffDays)}d overdue` };
  if (diffDays <= 30) return { key: "d30",    cls: "bg-rose-100 text-rose-700 border border-rose-200",         rowCls: "bg-rose-50/60",    label: `${diffDays}d` };
  if (diffDays <= 60) return { key: "d60",    cls: "bg-orange-100 text-orange-700 border border-orange-200",   rowCls: "bg-orange-50/50",  label: `${diffDays}d` };
  if (diffDays <= 90) return { key: "d90",    cls: "bg-yellow-100 text-yellow-800 border border-yellow-200",   rowCls: "bg-yellow-50/40",  label: `${diffDays}d` };
  return { key: "far", cls: "text-slate-600", rowCls: "", label: `${diffDays}d` };
}

function renderCell(p, col) {
  let v;
  if (col.custom) {
    v = p?.custom_fields?.[col.id];
  } else if (col.id === "start_end") {
    if (!p.start_date && !p.end_date) return "";
    return `${fmtDate(p.start_date)} – ${fmtDate(p.end_date)}`;
  } else {
    v = p?.[col.id];
  }
  if (col.id === "expiry_date") {
    const tone = expiryTone(v);
    return (
      <div className="flex items-center gap-1.5">
        <span>{fmtDate(v)}</span>
        {tone && <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full ${tone.cls}`}>{tone.label}</span>}
      </div>
    );
  }
  if (col.id === "coverage_time") {
    const m = coverageMonths(p);
    return m ? `${Number(m).toFixed(m >= 1 ? 0 : 1)} mo` : "";
  }
  if (col.type === "money") return fmtMoney(v);
  if (col.type === "percent") { const x = Number(v); return Number.isFinite(x) ? `${x.toFixed(3).replace(/\.?0+$/, "")}%` : ""; }
  if (col.type === "date")  return fmtDate(v);
  if (col.type === "number") return v == null || v === "" ? "" : String(v);
  return v || "";
}

function inputForType(col, value, onChange, testId, suggestions) {
  const commonCls = "w-full px-1.5 py-0.5 border border-slate-300 rounded text-xs bg-white";
  if (col.type === "money" || col.type === "number" || col.type === "percent" || col.type === "months") {
    return <input type="number" step="any" className={commonCls} value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} data-testid={testId} />;
  }
  if (col.type === "date") {
    return <input type="date" className={commonCls} value={value ? String(value).slice(0, 10) : ""} onChange={(e) => onChange(e.target.value || null)} data-testid={testId} />;
  }
  const listId = col.autocomplete && suggestions?.length ? `datalist-ins-${col.id}` : undefined;
  return (
    <>
      <input type="text" list={listId} className={commonCls} value={value ?? ""} onChange={(e) => onChange(e.target.value || null)} data-testid={testId} autoComplete="off" />
      {listId && (
        <datalist id={listId}>
          {suggestions.map((s) => <option key={s} value={s} />)}
        </datalist>
      )}
    </>
  );
}


function DraggableHeader({ col, sortBy, onSort, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: col.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const active = sortBy?.key === col.id;
  return (
    <th ref={setNodeRef} style={style} data-col-id={col.id} className="text-[10px] uppercase tracking-widest font-bold text-slate-500 px-2 py-1.5 border-b border-slate-200 whitespace-nowrap bg-white select-none group">
      <div className="flex items-center gap-1">
        <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-600" title="Drag to reorder column">
          <DotsSixVertical size={12} weight="bold" />
        </button>
        <button onClick={() => onSort && onSort(col.id)}
          className={`inline-flex items-center gap-1 hover:text-[#327de3] ${active ? "text-[#327de3]" : ""}`}
          data-testid={`ins-col-sort-${col.id}`} title="Sort by this column">
          <span>{col.label}</span>
          <ArrowsDownUp size={10} className={active ? "text-[#327de3]" : "text-slate-300"} weight={active ? "bold" : "regular"} />
        </button>
        {col.custom && onDelete && (
          <button onClick={(e) => { e.stopPropagation(); onDelete(col); }}
            className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity text-slate-300 hover:text-rose-600"
            title={`Delete "${col.label}" column`} data-testid={`ins-col-delete-${col.id}`}>
            <X size={11} weight="bold" />
          </button>
        )}
      </div>
    </th>
  );
}

export default function InsurancePage() {
  const { user } = useAuth();
  const [access, setAccess] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [search, setSearch] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [newRow, setNewRow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [openPolicy, setOpenPolicy] = useState(null);
  const [reminderMode, setReminderMode] = useState(false);
  const [reminderCfgOpen, setReminderCfgOpen] = useState(false);
  const [suggestions, setSuggestions] = useState({});
  const [showDeleted, setShowDeleted] = useState(false);
  const [showArchived, setShowArchived] = useState(true);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [pdfPickerOpen, setPdfPickerOpen] = useState(false);
  const [pdfCols, setPdfCols] = useState(null);
  const [toolbarOpen, setToolbarOpen] = useState(false);
  const [sortBy, setSortBy] = useState({ key: "insurance_id", dir: "asc" });
  const [customKpiMode, setCustomKpiMode] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [customKpiActive, setCustomKpiActive] = useState(false);
  const [customCols, setCustomCols] = useState([]);
  const [addColOpen, setAddColOpen] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [pendingDeleteCol, setPendingDeleteCol] = useState(null);
  const [typoPrompts, setTypoPrompts] = useState(null); // { rowKey, entries: [{col,label,input,suggestion}], onAcceptAll, onProceed }
  const tableWrapRef = useRef(null);

  const [colOrder, setColOrder] = useState(() => {
    try {
      const s = localStorage.getItem(COL_ORDER_LS_KEY);
      if (s) {
        const parsed = JSON.parse(s);
        const known = new Set(DEFAULT_COLS.map((c) => c.id));
        const merged = parsed.filter((id) => known.has(id) || id.startsWith("custom_"));
        for (const c of DEFAULT_COLS) if (!merged.includes(c.id)) merged.push(c.id);
        return merged;
      }
    } catch { /* ignore */ }
    return DEFAULT_COLS.map((c) => c.id);
  });
  useEffect(() => {
    localStorage.setItem(COL_ORDER_LS_KEY, JSON.stringify(colOrder));
  }, [colOrder]);

  const ALL_COLS = useMemo(() => {
    const custom = customCols.map((c) => ({
      id: c.key, label: c.label, type: c.type,
      custom: true, custom_id: c.id,
    }));
    return [...DEFAULT_COLS, ...custom];
  }, [customCols]);

  useEffect(() => {
    const known = new Set(ALL_COLS.map((c) => c.id));
    setColOrder((prev) => {
      const filtered = prev.filter((id) => known.has(id));
      for (const c of ALL_COLS) if (!filtered.includes(c.id)) filtered.push(c.id);
      if (filtered.length === prev.length && filtered.every((v, i) => v === prev[i])) return prev;
      return filtered;
    });
  }, [ALL_COLS]);

  const columns = useMemo(
    () => colOrder.map((id) => ALL_COLS.find((c) => c.id === id)).filter(Boolean),
    [colOrder, ALL_COLS],
  );

  // Access probe
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/insurance/access");
        setAccess(data);
      } catch {
        setAccess({ can_view: false });
      }
    })();
  }, []);

  const load = useCallback(async () => {
    try {
      const [act, dead] = await Promise.all([
        api.get("/insurance"),
        api.get("/insurance?deleted=true"),
      ]);
      setPolicies([...(act.data || []), ...(dead.data || [])]);
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
  }, []);

  const loadSuggestions = useCallback(async () => {
    const fields = [
      "insured_asset", "insured_entity", "type", "category", "type_code",
      "carrier", "broker", "broker_phone", "broker_email",
    ];
    const results = await Promise.all(fields.map((f) =>
      api.get(`/insurance/field-values?field=${f}`).then((r) => [f, r.data]).catch(() => [f, []])
    ));
    setSuggestions(Object.fromEntries(results));
  }, []);

  const loadCustomCols = useCallback(async () => {
    try {
      const { data } = await api.get("/insurance/columns");
      setCustomCols(Array.isArray(data) ? data : []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (access?.can_view) { load(); loadSuggestions(); loadCustomCols(); }
  }, [access?.can_view, load, loadSuggestions, loadCustomCols]);

  const active = useMemo(() => policies.filter((p) => !p.archived && !p.deleted), [policies]);
  const archived = useMemo(() => policies.filter((p) => p.archived && !p.deleted), [policies]);
  const deleted = useMemo(() => policies.filter((p) => p.deleted), [policies]);

  const filter = useCallback((list) => {
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((p) => {
      const hay = [
        p.insurance_id, p.insured_asset, p.insured_entity, p.type, p.category, p.type_code,
        p.broker, p.broker_email, p.broker_phone, p.carrier, p.policy_number, p.full_address,
        p.financing, ...Object.values(p.custom_fields || {}),
      ].map((v) => (v == null ? "" : String(v))).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [search]);

  const _applySort = useCallback((list) => {
    if (!sortBy.key) return list;
    const col = ALL_COLS.find((c) => c.id === sortBy.key);
    const arr = [...list];
    arr.sort((a, b) => {
      let av = col?.custom ? (a.custom_fields || {})[sortBy.key] : a[sortBy.key];
      let bv = col?.custom ? (b.custom_fields || {})[sortBy.key] : b[sortBy.key];
      if (av == null || av === "") return sortBy.dir === "asc" ? 1 : -1;
      if (bv == null || bv === "") return sortBy.dir === "asc" ? -1 : 1;
      if (col?.type === "date") { av = String(av); bv = String(bv); }
      else if (typeof av === "number" || (!isNaN(Number(av)) && col?.type !== "text")) { av = Number(av); bv = Number(bv); }
      else { av = String(av).toLowerCase(); bv = String(bv).toLowerCase(); }
      if (av === bv) return 0;
      const cmp = av > bv ? 1 : -1;
      return sortBy.dir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [sortBy, ALL_COLS]);

  const activeFiltered = useMemo(() => _applySort(filter(active)), [active, filter, _applySort]);
  const archivedFiltered = useMemo(() => _applySort(filter(archived)), [archived, filter, _applySort]);
  const deletedFiltered = useMemo(() => _applySort(filter(deleted)), [deleted, filter, _applySort]);

  // KPIs
  const kpis = useMemo(() => {
    const scope = customKpiActive ? active.filter((p) => selectedRows.has(p.id)) : active;
    let monthly = 0, annualised = 0;
    for (const p of scope) {
      const price = Number(p.premium_price);
      const months = coverageMonths(p);
      if (Number.isFinite(price) && months && months > 0) {
        const m = price / months;
        monthly += m;
        annualised += m * 12;
      }
    }
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const upcoming = scope
      .filter((p) => p.expiry_date)
      .map((p) => ({ ...p, _d: new Date(p.expiry_date + "T00:00:00") }))
      .filter((p) => !Number.isNaN(p._d) && p._d >= today)
      .sort((a, b) => a._d - b._d);
    return {
      count: scope.length,
      total_annual: annualised,
      monthly_equiv: monthly,
      next_expiration: upcoming[0] || null,
    };
  }, [active, customKpiActive, selectedRows]);

  // ---- add row / save with typo warning ----
  const startAddRow = () => {
    setEditMode(true);
    setNewRow({
      insured_asset: "", insured_entity: "", type: "", category: "", type_code: "",
      expiry_date: null, broker: "", broker_phone: "", broker_email: "",
      carrier: "", financing: "", start_date: null, end_date: null,
      coverage_time: null, premium_price: null, policy_number: "", full_address: "",
    });
    setTimeout(() => tableWrapRef.current?.querySelector('[data-testid="insurance-new-row-insured_asset"]')?.focus?.(), 40);
  };
  const cancelAdd = () => { setNewRow(null); setEditMode(false); };

  const _detectTypos = (row) => {
    const entries = [];
    for (const c of DEFAULT_COLS) {
      if (!TYPO_COLS.has(c.id)) continue;
      const val = row[c.id];
      if (val == null || val === "") continue;
      const opts = suggestions[c.id] || [];
      const sugg = findTypoMatch(val, opts);
      if (sugg) entries.push({ col: c.id, label: c.label, input: val, suggestion: sugg });
    }
    return entries;
  };

  const _postNewRow = async (row) => {
    setSaving(true);
    try {
      const { data } = await api.post("/insurance", row);
      toast.success(`Policy #${data.insurance_id} added`);
      setPolicies((prev) => [...prev, data].sort((a, b) => (a.insurance_id || 0) - (b.insurance_id || 0)));
      setNewRow(null); setEditMode(false);
      loadSuggestions();
    } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
    } finally { setSaving(false); }
  };

  const saveNewRow = async () => {
    if (!newRow) return;
    const entries = _detectTypos(newRow);
    if (entries.length) {
      setTypoPrompts({
        entries,
        onAcceptAll: async () => {
          const fixed = { ...newRow };
          for (const e of entries) fixed[e.col] = e.suggestion;
          setNewRow(fixed);
          setTypoPrompts(null);
          await _postNewRow(fixed);
        },
        onProceed: async () => {
          setTypoPrompts(null);
          await _postNewRow(newRow);
        },
      });
      return;
    }
    await _postNewRow(newRow);
  };

  // ---- archive / delete flow ----
  const toggleArchive = async (p) => {
    try {
      const { data } = await api.patch(`/insurance/${p.id}`, { field: "archived", value: !p.archived });
      setPolicies((prev) => prev.map((x) => (x.id === p.id ? data : x)));
      toast.success(p.archived ? "Policy restored to active" : "Policy archived");
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
  };

  const softDelete = async (p) => {
    if (!window.confirm(`Move policy #${p.insurance_id} to Deleted Insurance?`)) return;
    try {
      await api.delete(`/insurance/${p.id}`);
      setPolicies((prev) => prev.map((x) => (x.id === p.id ? { ...x, deleted: true } : x)));
      toast.success("Policy moved to Deleted Insurance");
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
  };

  const restoreDeleted = async (p) => {
    try {
      const { data } = await api.patch(`/insurance/${p.id}`, { field: "deleted", value: false });
      setPolicies((prev) => prev.map((x) => (x.id === p.id ? data : x)));
      toast.success("Policy restored");
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
  };

  const hardDelete = async (p) => {
    if (!window.confirm(`Permanently delete policy #${p.insurance_id}? This cannot be undone.`)) return;
    try {
      await api.delete(`/insurance/${p.id}?hard=true`);
      setPolicies((prev) => prev.filter((x) => x.id !== p.id));
      toast.success("Policy permanently deleted");
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
  };

  const toggleReminderFlag = async (p) => {
    try {
      const { data } = await api.patch(`/insurance/${p.id}`, { field: "email_reminder_enabled", value: !p.email_reminder_enabled });
      setPolicies((prev) => prev.map((x) => (x.id === p.id ? data : x)));
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
  };

  // ---- dnd column reorder ----
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setColOrder((prev) => arrayMove(prev, prev.indexOf(active.id), prev.indexOf(over.id)));
  };

  // ---- export ----
  const _exportStamp = () => {
    const now = new Date();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    const yy = String(now.getFullYear()).slice(-2);
    const time = now.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    return { filename: `${mm}.${dd}.${yy}`, header: `Exported On ${mm}/${dd}/${yy} ${time}` };
  };

  const exportExcel = () => {
    const { filename, header } = _exportStamp();
    const wb = XLSX.utils.book_new();
    const aoa = [
      [{ v: `INSURANCE · STRATA · ${header}`, s: { font: { bold: true } } }],
      [""],
      ["Insurance ID", ...columns.map((c) => c.label)],
    ];
    for (const p of [...active, ...archived, ...deleted]) {
      const row = [p.insurance_id];
      for (const c of columns) {
        let v = c.custom ? (p.custom_fields || {})[c.id] : p[c.id];
        if (v == null || v === "") { row.push(""); continue; }
        if (c.type === "money") row.push({ v: Number(v), t: "n", z: '"$"#,##0.00_);("$"#,##0.00);"-"' });
        else if (c.type === "percent") row.push({ v: Number(v) / 100, t: "n", z: "0.000%" });
        else if (c.type === "date") row.push({ v: String(v).slice(0, 10), t: "d", z: "mm/dd/yyyy" });
        else if (c.type === "number" || c.type === "months") row.push({ v: Number(v), t: "n" });
        else row.push(String(v));
      }
      aoa.push(row);
    }
    const ws = XLSX.utils.aoa_to_sheet(aoa, { cellDates: true });
    ws["!cols"] = [{ wch: 8 }, ...columns.map((c) => ({ wch: c.type === "text" ? 22 : 14 }))];
    ws["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: columns.length } }];
    XLSX.utils.book_append_sheet(wb, ws, "Insurance");
    XLSX.writeFile(wb, `INSURANCE STRATA excel_${filename}.xlsx`);
  };

  const exportPdf = (selectedColIds) => {
    const el = document.getElementById("insurance-export-root");
    if (!el) { toast.error("Nothing to export"); return; }
    const { filename, header } = _exportStamp();
    let styleEl = null;
    if (selectedColIds && selectedColIds.length) {
      const hide = colOrder.filter((id) => !selectedColIds.includes(id));
      if (hide.length) {
        const rules = hide.map((id) => `#insurance-export-root [data-col-id="${id}"] { display: none !important; }`).join("\n");
        styleEl = document.createElement("style");
        styleEl.textContent = rules;
        document.head.appendChild(styleEl);
      }
    }
    const banner = document.createElement("div");
    banner.style.cssText = "font-size:10px;color:#f87171;padding:4px 8px 6px;letter-spacing:1.2px;text-transform:uppercase;font-weight:600;text-align:left;";
    banner.textContent = header;
    el.prepend(banner);
    html2pdf().from(el).set({
      margin: [0.25, 0.25, 0.25, 0.25],
      filename: `INSURANCE STRATA PDF_${filename}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: "in", format: [11, 17], orientation: "landscape" },
    }).save().then(() => {
      banner.remove(); if (styleEl) styleEl.remove();
    }).catch(() => {
      banner.remove(); if (styleEl) styleEl.remove();
    });
  };

  const openPdfPicker = () => { setPdfCols(colOrder.slice()); setPdfPickerOpen(true); };

  if (access === null) {
    return (
      <div className="min-h-screen bg-[#F4F6F8]">
        <AppHeader />
        <div className="flex items-center justify-center py-32 text-sm text-slate-500">Loading…</div>
      </div>
    );
  }
  if (!access?.can_view) {
    return (
      <div className="min-h-screen bg-[#f5f7fb] flex flex-col">
        <AppHeader />
        <main className="flex-1 max-w-4xl mx-auto p-6">
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-sm text-amber-800">
            <div className="font-bold mb-1">Insurance access is disabled</div>
            Ask a super admin to enable "Insurance" for your account in Admin → Users.
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F6F8]">
      <AppHeader />
      <main className="max-w-none mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 2xl:px-10 py-3" data-testid="insurance-page">
        {/* Top bar */}
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <h1 className="text-xl font-bold text-slate-900 mr-2" data-testid="insurance-title">Insurance</h1>
          <div className="relative flex-1 min-w-0 max-w-[520px]">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search across every column…" className="pl-9 bg-white" data-testid="insurance-search" />
          </div>
          <div className="ml-auto flex items-center gap-1.5 flex-wrap">
            <Button size="sm" onClick={startAddRow} className="bg-[#1f64c2] hover:bg-[#1854a3]" data-testid="insurance-add">
              <Plus size={13} className="mr-1" /> Add policy
            </Button>
            <Button size="sm" variant="outline" onClick={() => setToolbarOpen((s) => !s)} data-testid="insurance-toolbar-toggle" title="More actions">
              <ListIcon size={14} className="mr-1" />
              {toolbarOpen ? <CaretUp size={11} /> : <CaretDown size={11} />}
            </Button>
          </div>
        </div>

        {/* Collapsible action toolbar */}
        {toolbarOpen && (
          <div className="mb-3 rounded-lg border border-slate-200 bg-white p-2 flex flex-wrap gap-1.5" data-testid="insurance-toolbar-panel">
            <div className="inline-flex items-center">
              <Button size="sm" variant={customKpiMode ? "default" : "outline"}
                onClick={() => {
                  if (customKpiMode) { setCustomKpiMode(false); setCustomKpiActive(false); setSelectedRows(new Set()); }
                  else setCustomKpiMode(true);
                }}
                className={customKpiMode ? "bg-orange-500 hover:bg-orange-600 rounded-r-none" : "border-orange-300 text-orange-700 hover:bg-orange-50 rounded-r-none border-r-0"}
                data-testid="insurance-custom-kpi" title="Toggle Custom KPI mode">
                Custom KPI
              </Button>
              <Button size="sm" variant={customKpiActive ? "default" : "outline"}
                onClick={() => { if (customKpiMode) setCustomKpiActive(true); }}
                disabled={!customKpiMode || selectedRows.size === 0}
                className={`rounded-l-none -ml-px ${customKpiActive ? "bg-orange-600 hover:bg-orange-700" : "border-orange-300 text-orange-700 hover:bg-orange-50"}`}
                data-testid="insurance-custom-kpi-refresh" title="Refresh KPIs using checked rows">
                <ArrowClockwise size={13} className="mr-1" /> Refresh
              </Button>
            </div>
            <Button size="sm" variant="outline" onClick={() => setBulkOpen(true)} data-testid="insurance-bulk-open">
              <FileXls size={14} className="mr-1.5" /> Bulk upload
            </Button>
            <Button size="sm" variant={editMode ? "default" : "outline"} onClick={editMode ? cancelAdd : startAddRow} data-testid="insurance-toggle-edit" className={editMode ? "bg-[#327de3] hover:bg-[#1f64c2]" : ""}>
              <PencilSimple size={14} className="mr-1.5" /> {editMode ? "Exit edit mode" : "Add / edit mode"}
            </Button>
            <Button size="sm" variant="outline" onClick={exportExcel} data-testid="insurance-export-excel">
              <FileXls size={14} className="mr-1.5" /> Export Excel
            </Button>
            <Button size="sm" variant="outline" onClick={openPdfPicker} data-testid="insurance-export-pdf">
              <FilePdf size={14} className="mr-1.5" /> Export PDF
            </Button>
            <Button size="sm" variant={reminderMode ? "default" : "outline"} onClick={() => setReminderMode((m) => !m)} data-testid="insurance-reminder-mode" className={reminderMode ? "bg-amber-500 hover:bg-amber-600" : ""}>
              <Bell size={14} className="mr-1.5" /> {reminderMode ? "Exit reminder mode" : "Reminder flags"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setReminderCfgOpen(true)} data-testid="insurance-email-reminders">
              <Bell size={14} className="mr-1.5" /> Email reminders
            </Button>
          </div>
        )}

        <div id="insurance-export-root">
        {/* KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-2 mb-3" data-testid="insurance-kpis">
          <KpiCard title="Total Insurance Policies" value={kpis.count.toLocaleString()} testId="kpi-count" />
          <KpiCard title="Total Annual Premium" value={fmtMoney(kpis.total_annual)} testId="kpi-annual" />
          <KpiCard title="Monthly Equivalent Premium" value={fmtMoney(kpis.monthly_equiv)} testId="kpi-monthly" />
          <KpiCard
            title="Next Expiration Date"
            value={kpis.next_expiration ? fmtDate(kpis.next_expiration.expiry_date) : "—"}
            secondaryLines={kpis.next_expiration ? [
              `${kpis.next_expiration.insured_asset || "—"} · #${kpis.next_expiration.insurance_id}`,
              `Type Code: ${kpis.next_expiration.type_code || "—"}`,
              `Coverage: ${(() => { const m = coverageMonths(kpis.next_expiration); return m ? `${Number(m).toFixed(m >= 1 ? 0 : 1)} mo` : "—"; })()}`,
              `Premium: ${fmtMoney(kpis.next_expiration.premium_price) || "—"}`,
            ] : ["No upcoming policies"]}
            testId="kpi-next-expiry"
          />
          <ExpirationLegend />
        </div>

        {/* Section header */}
        <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
          <SectionHeader label={`Active insurance (${activeFiltered.length}${search ? ` of ${active.length}` : ""})`} />
          <div className="flex items-center gap-1.5">
            <Button size="sm" variant="outline" onClick={() => setAddColOpen(true)} className="text-[11px] h-7 px-2 border-slate-300 text-slate-700 hover:bg-slate-100" data-testid="insurance-add-column" title="Add a custom column">
              <Plus size={11} className="mr-1" /> Add column
            </Button>
            <Button size="sm" variant="outline" onClick={() => setRestoreOpen(true)} className="text-[11px] h-7 px-2 border-slate-300 text-slate-700 hover:bg-slate-100" data-testid="insurance-restore-columns" title="Restore a previously-deleted column">
              <ArrowClockwise size={11} className="mr-1" /> Restore custom columns
            </Button>
          </div>
        </div>

        {/* Active table */}
        <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto" ref={tableWrapRef}>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <table className="w-full text-xs" data-testid="insurance-table-active">
              <thead>
                <tr className="bg-slate-50">
                  {customKpiMode && <th className="text-[10px] uppercase tracking-widest font-bold text-orange-700 px-2 py-1.5 border-b border-slate-200 bg-orange-100 sticky left-0 z-10">✓</th>}
                  <th className={`text-[10px] uppercase tracking-widest font-bold text-slate-500 px-2 py-1.5 border-b border-slate-200 sticky ${customKpiMode ? "left-10" : "left-0"} bg-slate-50 z-10`}>Insurance ID</th>
                  <th className="text-[10px] uppercase tracking-widest font-bold text-slate-500 px-2 py-1.5 border-b border-slate-200 whitespace-nowrap" title="Last Updated">LU</th>
                  <th className="text-[10px] uppercase tracking-widest font-bold text-slate-500 px-2 py-1.5 border-b border-slate-200 whitespace-nowrap text-center">Attach.</th>
                  {reminderMode && <th className="text-[10px] uppercase tracking-widest font-bold text-amber-600 px-2 py-1.5 border-b border-slate-200 whitespace-nowrap"><Bell size={11} className="inline" /> Reminder</th>}
                  <SortableContext items={colOrder} strategy={horizontalListSortingStrategy}>
                    {columns.map((c) => (
                      <DraggableHeader key={c.id} col={c} sortBy={sortBy}
                        onSort={(k) => setSortBy((s) => ({ key: k, dir: s.key === k && s.dir === "asc" ? "desc" : "asc" }))}
                        onDelete={(col) => setPendingDeleteCol(col)} />
                    ))}
                  </SortableContext>
                  <th className="text-[10px] uppercase tracking-widest font-bold text-slate-500 px-2 py-1.5 border-b border-slate-200 whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody>
                {editMode && newRow && (
                  <tr className="bg-[#eaf2ff]/40" data-testid="insurance-new-row">
                    {customKpiMode && <td className="px-2 py-0.5 border-b border-slate-100"></td>}
                    <td className="px-2 py-0.5 border-b border-slate-100 text-[11px] font-bold text-slate-500 sticky left-0 bg-[#eaf2ff]/60">NEW</td>
                    <td className="px-2 py-0.5 border-b border-slate-100"></td>
                    <td className="px-2 py-0.5 border-b border-slate-100"></td>
                    {reminderMode && <td className="px-2 py-0.5 border-b border-slate-100"></td>}
                    {columns.map((c) => (
                      <td key={c.id} data-col-id={c.id} className="px-1.5 py-0.5 border-b border-slate-100 min-w-[110px]">
                        {c.custom
                          ? inputForType(c,
                              (newRow.custom_fields || {})[c.id],
                              (v) => setNewRow((r) => ({ ...r, custom_fields: { ...(r.custom_fields || {}), [c.id]: v } })),
                              `insurance-new-row-${c.id}`, undefined)
                          : inputForType(c, newRow[c.id],
                              (v) => setNewRow((r) => ({ ...r, [c.id]: v })),
                              `insurance-new-row-${c.id}`, suggestions[c.id])}
                      </td>
                    ))}
                    <td className="px-2 py-0.5 border-b border-slate-100 whitespace-nowrap">
                      <Button size="sm" onClick={saveNewRow} disabled={saving} className="h-6 px-2 text-[10px] bg-[#327de3] hover:bg-[#1f64c2]" data-testid="insurance-new-row-save">Save</Button>
                      <Button size="sm" variant="ghost" onClick={cancelAdd} className="h-6 px-2 text-[10px] ml-1"><X size={11} /></Button>
                    </td>
                  </tr>
                )}
                {activeFiltered.map((p) => (
                  <PolicyRow key={p.id} policy={p} columns={columns}
                    onOpen={() => setOpenPolicy(p)}
                    onArchive={() => toggleArchive(p)}
                    reminderMode={reminderMode}
                    onToggleReminder={() => toggleReminderFlag(p)}
                    customKpiMode={customKpiMode}
                    selected={selectedRows.has(p.id)}
                    onToggleSelect={() => setSelectedRows((s) => { const n = new Set(s); if (n.has(p.id)) n.delete(p.id); else n.add(p.id); return n; })} />
                ))}
                {activeFiltered.length === 0 && !newRow && (
                  <tr>
                    <td colSpan={columns.length + 4 + (reminderMode ? 1 : 0) + (customKpiMode ? 1 : 0)} className="text-center text-slate-400 italic py-6 text-xs">
                      {search ? "No policies match your search." : "No insurance policies yet. Click \"Add policy\" to create the first one."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </DndContext>
        </div>

        {/* Archived */}
        <div className="mt-4">
          <button type="button" onClick={() => setShowArchived((s) => !s)}
            className="text-[11px] uppercase tracking-widest font-bold text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
            data-testid="insurance-archived-toggle">
            <span className={`inline-block transition-transform ${showArchived ? "rotate-90" : ""}`}>▸</span>
            Archived insurance ({archivedFiltered.length}{search ? ` of ${archived.length}` : ""})
          </button>
          {showArchived && (
            <div className="mt-1 rounded-lg border border-slate-200 bg-slate-50/60 overflow-x-auto">
              <table className="w-full text-xs" data-testid="insurance-table-archived">
                <thead>
                  <tr className="bg-slate-100/70">
                    <th className="text-[10px] uppercase tracking-widest font-bold text-slate-500 px-2 py-1.5 border-b border-slate-200">Insurance ID</th>
                    <th className="text-[10px] uppercase tracking-widest font-bold text-slate-500 px-2 py-1.5 border-b border-slate-200 whitespace-nowrap" title="Last Updated">LU</th>
                    <th className="text-[10px] uppercase tracking-widest font-bold text-slate-500 px-2 py-1.5 border-b border-slate-200 whitespace-nowrap text-center">Attach.</th>
                    {columns.map((c) => <th key={c.id} data-col-id={c.id} className="text-[10px] uppercase tracking-widest font-bold text-slate-500 px-2 py-1.5 border-b border-slate-200 whitespace-nowrap">{c.label}</th>)}
                    <th className="text-[10px] uppercase tracking-widest font-bold text-slate-500 px-2 py-1.5 border-b border-slate-200 whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {archivedFiltered.map((p) => (
                    <PolicyRow key={p.id} policy={p} columns={columns}
                      onOpen={() => setOpenPolicy(p)}
                      onArchive={() => toggleArchive(p)}
                      onDelete={() => softDelete(p)} dim />
                  ))}
                  {archivedFiltered.length === 0 && (
                    <tr><td colSpan={columns.length + 4} className="text-center text-slate-400 italic py-4 text-xs">{search ? "No archived policies match." : "No archived policies."}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Deleted */}
        <div className="mt-3">
          <button type="button" onClick={() => setShowDeleted((s) => !s)}
            className="text-[10px] uppercase tracking-widest font-semibold text-slate-400 hover:text-slate-600 inline-flex items-center gap-1"
            data-testid="insurance-deleted-toggle">
            <span className={`inline-block transition-transform ${showDeleted ? "rotate-90" : ""}`}>▸</span>
            Deleted insurance ({deletedFiltered.length}{search ? ` of ${deleted.length}` : ""})
          </button>
          {showDeleted && (
            <div className="mt-1 rounded-md border border-slate-200 bg-slate-50/40 overflow-x-auto">
              <table className="w-full text-[10px]" data-testid="insurance-table-deleted">
                <thead>
                  <tr className="bg-slate-100/50">
                    <th className="text-[9px] uppercase tracking-widest font-semibold text-slate-400 px-1.5 py-1 border-b border-slate-200">#</th>
                    <th className="text-[9px] uppercase tracking-widest font-semibold text-slate-400 px-1.5 py-1 border-b border-slate-200 whitespace-nowrap">Last Upd.</th>
                    <th className="text-[9px] uppercase tracking-widest font-semibold text-slate-400 px-1.5 py-1 border-b border-slate-200 whitespace-nowrap text-center">Att.</th>
                    {columns.map((c) => <th key={c.id} data-col-id={c.id} className="text-[9px] uppercase tracking-widest font-semibold text-slate-400 px-1.5 py-1 border-b border-slate-200 whitespace-nowrap">{c.label}</th>)}
                    <th className="text-[9px] uppercase tracking-widest font-semibold text-slate-400 px-1.5 py-1 border-b border-slate-200">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {deletedFiltered.map((p) => (
                    <tr key={p.id} className="opacity-60 hover:opacity-100" data-testid={`insurance-deleted-row-${p.id}`}>
                      <td className="px-1.5 py-0.5 border-b border-slate-100 font-semibold text-slate-500 whitespace-nowrap">#{p.insurance_id}</td>
                      <td className="px-1.5 py-0.5 border-b border-slate-100 whitespace-nowrap text-slate-500">{fmtLU(p.updated_at)}</td>
                      <td className="px-1.5 py-0.5 border-b border-slate-100 whitespace-nowrap text-center">{p.has_attachments ? <Paperclip size={11} className="inline text-slate-500" /> : <span className="inline-block w-4 h-2 bg-rose-200 border border-rose-300 rounded-sm" />}</td>
                      {columns.map((c) => (
                        <td key={c.id} data-col-id={c.id} className="px-1.5 py-0.5 border-b border-slate-100 whitespace-nowrap">{renderCell(p, c)}</td>
                      ))}
                      <td className="px-1.5 py-0.5 border-b border-slate-100 whitespace-nowrap">
                        <button onClick={() => restoreDeleted(p)} className="text-[9px] font-semibold text-emerald-700 hover:underline mr-1" data-testid={`insurance-deleted-restore-${p.id}`}>Restore</button>
                        {user?.is_super_admin && (
                          <button onClick={() => hardDelete(p)} className="text-[9px] font-semibold text-rose-600 hover:underline" data-testid={`insurance-deleted-hard-${p.id}`}>Purge</button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {deletedFiltered.length === 0 && (
                    <tr><td colSpan={columns.length + 4} className="text-center text-slate-400 italic py-2 text-[10px]">No deleted policies.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </div>
      </main>

      <InsuranceModal
        policy={openPolicy}
        customCols={customCols}
        suggestions={suggestions}
        onClose={() => { setOpenPolicy(null); load(); loadSuggestions(); }}
        onChange={(updated) => {
          setPolicies((prev) => prev.map((x) => x.id === updated.id ? updated : x));
          setOpenPolicy(updated);
        }}
      />

      <AddColumnModal open={addColOpen} onClose={() => setAddColOpen(false)} onCreated={() => loadCustomCols()} />
      <RestoreColumnsModal open={restoreOpen} onClose={() => setRestoreOpen(false)} onRestored={() => loadCustomCols()} />
      <BulkUploadModal open={bulkOpen} onClose={() => setBulkOpen(false)} onImported={() => { load(); loadSuggestions(); }} />
      <ReminderConfigModal open={reminderCfgOpen} onClose={() => setReminderCfgOpen(false)} isSuperAdmin={!!user?.is_super_admin} />

      {/* Delete column confirmation */}
      <Dialog open={!!pendingDeleteCol} onOpenChange={(o) => !o && setPendingDeleteCol(null)}>
        <DialogContent className="max-w-md" data-testid="insurance-delete-column-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-600">
              <WarningCircle size={18} weight="fill" /> Delete "{pendingDeleteCol?.label}" column?
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-700 space-y-2">
            <p><strong>This action can't be undone from here.</strong> The column disappears from the table right away.</p>
            <p className="text-[12px] text-slate-500">
              All cell values across every row stay safe on the server. Use <em>Restore custom columns</em> to bring the column and its data back.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPendingDeleteCol(null)}>Cancel</Button>
            <Button
              onClick={async () => {
                const c = pendingDeleteCol;
                if (!c) return;
                try {
                  await api.delete(`/insurance/columns/${c.custom_id}`);
                  toast.success(`Column "${c.label}" deleted. Values preserved.`);
                  setPendingDeleteCol(null);
                  loadCustomCols();
                } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
              }}
              className="bg-rose-600 hover:bg-rose-700" data-testid="insurance-delete-column-confirm">
              Delete column
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TypoWarningDialog prompts={typoPrompts} onCancel={() => setTypoPrompts(null)} />

      {/* PDF picker */}
      {pdfPickerOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setPdfPickerOpen(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 bg-gradient-to-r from-[#1f64c2] to-[#327de3] text-white flex items-center gap-2">
              <FilePdf size={18} />
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-widest opacity-80">Export</div>
                <div className="text-base font-bold">Pick columns for the PDF</div>
              </div>
              <button onClick={() => setPdfPickerOpen(false)} className="p-1 rounded hover:bg-white/10"><X size={16} /></button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              <div className="text-[11px] text-slate-500 mb-2">Uncheck any columns you'd like to hide in the PDF. Insurance ID and Actions are always included.</div>
              <div className="flex items-center gap-2 mb-2">
                <button onClick={() => setPdfCols(colOrder.slice())} className="text-[11px] font-semibold text-[#1f64c2] hover:underline">Select all</button>
                <span className="text-slate-300">·</span>
                <button onClick={() => setPdfCols([])} className="text-[11px] font-semibold text-[#1f64c2] hover:underline">Deselect all</button>
                <span className="ml-auto text-[10px] text-slate-500">{pdfCols?.length || 0} of {colOrder.length} selected</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                {colOrder.map((id) => {
                  const c = ALL_COLS.find((x) => x.id === id);
                  if (!c) return null;
                  const on = pdfCols?.includes(id);
                  return (
                    <label key={id} className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded border cursor-pointer ${on ? "bg-[#eaf2ff] border-[#327de3]" : "bg-white border-slate-200"}`}>
                      <input type="checkbox" checked={on}
                        onChange={() => setPdfCols((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))}
                        className="accent-[#327de3]" />
                      <span className="flex-1">{c.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
            <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setPdfPickerOpen(false)}>Cancel</Button>
              <Button onClick={() => { setPdfPickerOpen(false); setTimeout(() => exportPdf(pdfCols), 100); }}
                className="bg-[#327de3] hover:bg-[#1f64c2]" disabled={!pdfCols?.length}>
                <FilePdf size={13} className="mr-1.5" /> Export PDF
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function KpiCard({ title, value, secondary, secondaryLines, testId }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 relative" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{title}</div>
      <div className="text-lg font-bold leading-tight mt-0.5 text-slate-900" data-testid={`${testId}-value`}>{value}</div>
      {secondaryLines
        ? <div className="mt-0.5 space-y-0.5">{secondaryLines.map((s, i) => <div key={i} className="text-[10px] text-slate-500 truncate" title={s}>{s}</div>)}</div>
        : secondary && <div className="text-[11px] text-slate-500 mt-1 truncate" title={secondary}>{secondary}</div>}
    </div>
  );
}

function ExpirationLegend() {
  return (
    <div className="rounded-lg bg-white border border-slate-200 px-3 py-2 text-[10px]" data-testid="expiration-legend">
      <div className="uppercase tracking-widest text-slate-500 font-semibold mb-1">Expiration legend</div>
      <div className="flex flex-wrap gap-1">
        <span className="bg-rose-100 text-rose-700 border border-rose-200 px-1.5 py-0.5 rounded-full font-semibold">≤30 d</span>
        <span className="bg-orange-100 text-orange-700 border border-orange-200 px-1.5 py-0.5 rounded-full font-semibold">≤60 d</span>
        <span className="bg-yellow-100 text-yellow-800 border border-yellow-200 px-1.5 py-0.5 rounded-full font-semibold">≤90 d</span>
        <span className="bg-rose-600 text-white px-1.5 py-0.5 rounded-full font-semibold">Expired</span>
      </div>
    </div>
  );
}

function SectionHeader({ label }) {
  return <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-1.5 mt-1">{label}</div>;
}

function PolicyRow({ policy, columns, onOpen, onArchive, onDelete, dim, reminderMode, onToggleReminder, customKpiMode, selected, onToggleSelect }) {
  const archived = !!policy.archived && !policy.deleted;
  const lastUpd = fmtLU(policy.updated_at);
  const tone = expiryTone(policy.expiry_date);
  const expiryRowCls = !archived && tone ? tone.rowCls : "";
  return (
    <tr
      className={`hover:bg-[#eaf2ff]/40 cursor-pointer ${dim ? "opacity-70" : ""} ${archived ? "bg-emerald-50/70" : expiryRowCls} ${policy.email_reminder_enabled ? "bg-amber-50/40" : ""} ${selected ? "bg-orange-50" : ""}`}
      onClick={onOpen}
      data-testid={`insurance-row-${policy.id}`}
    >
      {customKpiMode && (
        <td className={`px-2 py-0.5 border-b border-slate-100 sticky left-0 z-[2] ${selected ? "bg-orange-100" : "bg-orange-50"}`} onClick={(e) => e.stopPropagation()}>
          <input type="checkbox" checked={!!selected}
            onChange={(e) => { e.stopPropagation(); onToggleSelect(); }}
            onClick={(e) => e.stopPropagation()}
            className="w-3.5 h-3.5 accent-orange-500 cursor-pointer"
            data-testid={`insurance-select-${policy.id}`} />
        </td>
      )}
      <td className={`px-2 py-0.5 border-b border-slate-100 font-bold text-slate-700 sticky ${customKpiMode ? "left-10" : "left-0"} z-[1] whitespace-nowrap ${archived ? "bg-emerald-50" : "bg-white"}`}>
        #{policy.insurance_id}
      </td>
      <td className="px-2 py-0.5 border-b border-slate-100 whitespace-nowrap text-[11px] text-slate-600">{lastUpd}</td>
      <td className="px-2 py-0.5 border-b border-slate-100 whitespace-nowrap text-center">
        {policy.has_attachments
          ? <Paperclip size={14} weight="bold" className="inline text-slate-600" />
          : <span className="inline-block w-5 h-3 bg-rose-200 border border-rose-300 rounded-sm" title="No attachments — please upload" />}
      </td>
      {reminderMode && (
        <td className="px-2 py-0.5 border-b border-slate-100 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
          <label className="inline-flex items-center gap-1 cursor-pointer">
            <input type="checkbox" checked={!!policy.email_reminder_enabled} onChange={onToggleReminder} className="w-3.5 h-3.5 accent-amber-500" data-testid={`insurance-reminder-toggle-${policy.id}`} />
            {policy.email_reminder_enabled && <span className="text-[10px] font-bold uppercase text-amber-700">ON</span>}
          </label>
        </td>
      )}
      {columns.map((c) => (
        <td key={c.id} data-col-id={c.id} className="px-2 py-0.5 border-b border-slate-100 whitespace-nowrap">{renderCell(policy, c)}</td>
      ))}
      <td className="px-2 py-0.5 border-b border-slate-100 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-1">
          <button onClick={onArchive}
            className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${archived ? "bg-white border-emerald-300 text-emerald-700 hover:bg-emerald-100" : "bg-white border-slate-200 text-slate-500 hover:border-rose-300 hover:text-rose-600"}`}
            data-testid={`insurance-archive-${policy.id}`}
            title={archived ? "Restore to active" : "Archive this policy"}>
            <Archive size={10} className="inline mr-0.5" />
            {archived ? "Restore" : "Archive"}
          </button>
          {archived && onDelete && (
            <button onClick={onDelete}
              className="text-[10px] font-semibold px-1.5 py-0.5 rounded border bg-white border-slate-200 text-slate-500 hover:border-rose-400 hover:text-rose-600"
              data-testid={`insurance-delete-${policy.id}`}
              title="Move to Deleted Insurance">
              Delete
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}
